<template>
  <v-row justify="center">
    <v-dialog
      v-model="isVisible"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="500"
      persistent
      scrollable
    >
      <v-card color="brand-card-bg">
        <v-toolbar
          color="transparent"
          flat
          height="70"
          max-height="70"
        >
          <v-toolbar-title class="pl-3 brand-green-dark--text">
            <div class="brand-text-title mt-1">
              {{ $t('Auth.LoginDialog.Title').toLocaleUpperCase($i18n.locale) }}
            </div>

            <div
              v-if="$t('Auth.LoginDialog.Subtitle')"
              class="text-body brand-grey--text mt-1"
            >
              {{ $t('Auth.LoginDialog.Subtitle') }}
            </div>
          </v-toolbar-title>

          <v-spacer />

          <v-btn
            :height="$vuetify.breakpoint.smAndDown ? 40 : 50"
            :width="$vuetify.breakpoint.smAndDown ? 40 : 50"
            class="ma-0"
            color="brand-green-dark"
            icon
            outlined
            style="background-color: white;"
            @click="closeDialog"
          >
            <v-icon small>
              mdi-close
            </v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-4 pt-0 pb-2">
          <v-divider class="brand-green-dark" />

          <v-form
            v-if="model"
            ref="form"
            v-model="isFormValid"
            @submit.prevent
          >
            <v-container fluid>
              <v-row dense>
                <v-col
                  class="py-4 brand-text-small brand-grey--text"
                  cols="12"
                >
                  {{ $t('Auth.LoginDialog.Social.Title') }}
                </v-col>
              </v-row>

              <v-row
                v-if="facebookLoginProviderEnabled || googleLoginProviderEnabled"
                dense
              >
                <v-col
                  v-if="googleLoginProviderEnabled"
                  cols="12"
                >
                  <v-btn
                    :class="{'white': $vuetify.theme.dark}"
                    block
                    class="black text-none px-2"
                    large
                    outlined
                    @click="authSocialProvider('google')"
                  >
                    <div class="d-flex align-center">
                      <div class="d-inline-flex mr-2">
                        <svg
                          height="22px"
                          preserveAspectRatio="xMidYMid"
                          viewBox="-3 0 262 262"
                          width="22px"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <g stroke-width="0" />
                          <g
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <g>
                            <path
                              d="M255.878 133.451c0-10.734-.871-18.567-2.756-26.69H130.55v48.448h71.947c-1.45 12.04-9.283 30.172-26.69 42.356l-.244 1.622 38.755 30.023 2.685.268c24.659-22.774 38.875-56.282 38.875-96.027"
                              fill="#4285F4"
                            />
                            <path
                              d="M130.55 261.1c35.248 0 64.839-11.605 86.453-31.622l-41.196-31.913c-11.024 7.688-25.82 13.055-45.257 13.055-34.523 0-63.824-22.773-74.269-54.25l-1.531.13-40.298 31.187-.527 1.465C35.393 231.798 79.49 261.1 130.55 261.1"
                              fill="#34A853"
                            />
                            <path
                              d="M56.281 156.37c-2.756-8.123-4.351-16.827-4.351-25.82 0-8.994 1.595-17.697 4.206-25.82l-.073-1.73L15.26 71.312l-1.335.635C5.077 89.644 0 109.517 0 130.55s5.077 40.905 13.925 58.602l42.356-32.782"
                              fill="#FBBC05"
                            />
                            <path
                              d="M130.55 50.479c24.514 0 41.05 10.589 50.479 19.438l36.844-35.974C195.245 12.91 165.798 0 130.55 0 79.49 0 35.393 29.301 13.925 71.947l42.211 32.783c10.59-31.477 39.891-54.251 74.414-54.251"
                              fill="#EB4335"
                            />
                          </g>
                        </svg>
                      </div>

                      <div class="d-inline-flex white--text brand-text-title">
                        {{ $t('Auth.LoginDialog.Social.Google').toLocaleUpperCase($i18n.locale) }}
                      </div>
                    </div>
                  </v-btn>
                </v-col>

                <v-col
                  v-if="facebookLoginProviderEnabled"
                  cols="12"
                >
                  <v-btn
                    block
                    class="text-none px-2 mt-1"
                    color="blue darken-2"
                    dark
                    depressed
                    large
                    @click="authSocialProvider('facebook')"
                  >
                    <div class="d-flex align-center">
                      <div class="d-inline-flex mr-2">
                        <v-icon>
                          mdi-facebook
                        </v-icon>
                      </div>

                      <div class="d-inline-flex white--text brand-text-title">
                        {{ $t('Auth.LoginDialog.Social.Facebook').toLocaleUpperCase($i18n.locale) }}
                      </div>
                    </div>
                  </v-btn>
                </v-col>
              </v-row>

              <template v-if="emailLoginProviderEnabled">
                <v-row no-gutters>
                  <v-col cols="12">
                    <v-separator class="mb-4 mt-8 brand-text-small brand-grey--text">
                      {{ $t('Auth.LoginDialog.Account.Title') }}
                    </v-separator>
                  </v-col>
                </v-row>

                <v-row dense>
                  <v-col cols="12">
                    <v-text-field
                      v-model="model.Username"
                      :hint="$t('Auth.LoginDialog.Account.Field.Email.Hint')"
                      :label="$t('Auth.LoginDialog.Account.Field.Email.Label')"
                      :placeholder="$t('Auth.LoginDialog.Account.Field.Email.Placeholder')"
                      :rules="model.validator.vuetifyFormFieldRules('Username')"
                      clearable
                      dense
                      name="Username"
                      outlined
                      type="text"
                    />
                  </v-col>

                  <v-col cols="12">
                    <v-text-field
                      v-model="model.Password"
                      :hint="$t('Auth.LoginDialog.Account.Field.Password.Hint')"
                      :label="$t('Auth.LoginDialog.Account.Field.Password.Label')"
                      :placeholder="$t('Auth.LoginDialog.Account.Field.Password.Placeholder')"
                      :rules="model.validator.vuetifyFormFieldRules('Password')"
                      clearable
                      dense
                      name="Password"
                      outlined
                      type="password"
                    />
                  </v-col>

                  <v-col
                    v-if="showGenericError && genericError"
                    class="py-0"
                    cols="12"
                  >
                    <v-alert
                      border="left"
                      dense
                      text
                      type="error"
                    >
                      {{ genericError }}
                    </v-alert>
                  </v-col>
                </v-row>
              </template>

              <v-row dense>
                <v-col cols="12">
                  <v-btn
                    :disabled="isFormSaving || !isFormValid"
                    :loading="isFormSaving"
                    block
                    class="brand-green-dark--text"
                    color="brand-primary"
                    depressed
                    x-large
                    @click="onLoginClick"
                  >
                    <span class="brand-text-heading">{{ $t('Auth.Button.Login').toLocaleUpperCase($i18n.locale) }}</span>
                  </v-btn>
                </v-col>

                <v-col cols="12">
                  <v-btn
                    block
                    class="text-none mt-3"
                    color="brand-green-dark"
                    plain
                    small
                    @click="onForgotPasswordClick"
                  >
                    <span class="brand-text-small">{{ $t('Auth.Button.ForgotPassword') }}</span>
                  </v-btn>

                  <v-btn
                    block
                    class="text-none"
                    color="brand-green-dark"
                    plain
                    small
                    @click="onRegisterClick"
                  >
                    <span class="brand-text-small">{{ $t('Auth.Button.NoAccount') }}</span>
                  </v-btn>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>

import LoginDialogMixin from '@/theme/default/components/login/LoginDialog.js'

export default {
  name  : 'LoginDialog',
  mixins: [LoginDialogMixin]
}
</script>

<style scoped src="@/theme/default/components/login/LoginDialog.css"></style>

<style scoped>
</style>
